/**
 * ChatStream — replica app.jsx:201-214 (.chat container + map messages).
 *
 * Mapea Messages[] del store:
 *   role='me' && text       → <Bubble variant="me">
 *   role='staff' && card     → <ProductCard> + <SubstitutesCard?> + <ComplementsCard?>
 *   role='system' && text    → <Bubble variant="system">
 *
 * Al final: si sseState busy → <LoadingStageMessage> + <CancelButton>.
 *           si sseState.error → <ErrorInlineMessage>.
 *
 * Auto-scroll bottom-on-update vía useEffect ref scrollIntoView.
 *
 * 2026-05-08 — Cart eliminado. Click en alternativa/complemento dispara nueva
 * consulta sobre ese SKU/PA (callback onPickAlternative del padre App). Demo
 * de consulta conversacional, no de venta.
 */
import { useEffect, useRef, useState } from 'react'
import type { Message } from '../store/chat'
import type { SseState } from '../store/sseReducer'
import { Bubble } from './Bubble'
import { LoadingStageMessage } from './LoadingStageMessage'
import { CancelButton } from './CancelButton'
import { ErrorInlineMessage } from './ErrorInlineMessage'
import { ProductCard } from './ProductCard'
import { OpcionesProducto } from './OpcionesProducto'
import { DerivacionBanner } from './DerivacionBanner'
import { FeedbackWidget } from './FeedbackWidget'
import { CierreRespuesta } from './CierreRespuesta'
import { AccionesProducto } from './AccionesProducto'

// los pedidos que manda App al tocar una acción (handleAccion)
const ACCIONES = /^(Más barato|Promoción|Más margen|Otra forma|En .+|Pesa .+ kg)$/
import { GuiaCliente } from './GuiaCliente'
import type { AccionCaso } from '../api/accion'
import type { ProductCard as ProductCardSchema } from '../types/product_card'

interface Props {
  messages: Message[]
  sseState: SseState
  onRetry: () => void
  /** 2026-05-08 — click en alternativa o complemento envía nueva consulta
   *  con el nombre/PA del item como prompt. Reemplaza el ex-comportamiento
   *  cart-based (PR #18 deshecho con PR cart-removal). */
  onPickAlternative: (text: string) => void
  /** 2026-05-12 — fluidez: chip "¿Algo más para este cliente?" post-card
   *  focusea el textarea sin enviar query vacío. Foca + scroll-to-end. */
  onContinue?: () => void
  /** Plan 04-03 — device_id de la tablet (useDeviceCreds). Pasado al
   *  ProductCard para que FeedbackWidget pueda hacer POST /feedback. Si
   *  ausente (no creds), el widget no rendea. */
  device_id?: string
  /** 2026-10-01 — pantalla ancha: la ficha completa vive en el panel derecho; acá va un
   *  resumen tocable. `fichaActiva` = índice del mensaje cuya ficha está en el panel. */
  fichaEnPanel?: boolean
  fichaActiva?: number | null
  onVerFicha?: (idx: number) => void
  /** 2026-10-01 — cierre de la última respuesta: acciones instantáneas + puertas. */
  perfil?: string | null
  onAccion?: (accion: AccionCaso, extra?: { forma?: string; peso_kg?: number }) => void
  onNuevoCliente?: () => void
}

function timeOf(ts: number): string {
  const d = new Date(ts)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export function ChatStream({
  messages,
  sseState,
  onRetry,
  onPickAlternative,
  onContinue,
  device_id,
  fichaEnPanel = false,
  fichaActiva = null,
  onVerFicha,
  perfil = null,
  onAccion,
  onNuevoCliente,
}: Props) {
  // 2026-10-01 — lectura: al preguntar, la pregunta sube arriba y la respuesta aparece
  // debajo (se lee de arriba hacia abajo, sin volver a subir). Si la regente subió a
  // releer algo, no se la mueve: aparece "Nueva respuesta" para ir cuando quiera.
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const manualRef = useRef(false)
  const programadoRef = useRef(false)
  const [pendiente, setPendiente] = useState<number | null>(null)
  const prevLen = useRef(messages.length)

  const llevarArriba = (idx: number) => {
    const box = scrollRef.current
    const el = box?.querySelector<HTMLElement>(`[data-msg="${idx}"]`)
    if (!box || !el) return
    programadoRef.current = true
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    box.scrollTo({ top: Math.max(0, el.offsetTop - 12), behavior: reduce ? 'auto' : 'smooth' })
    window.setTimeout(() => { programadoRef.current = false }, 600)
  }

  useEffect(() => {
    const n = messages.length
    if (n <= prevLen.current) {
      prevLen.current = n
      return
    }
    const restaurado = n - prevLen.current > 2  // "Deshacer" de Nuevo cliente: no es una respuesta nueva
    prevLen.current = n
    const ultimo = messages[n - 1]
    let pregunta = -1
    for (let i = n - 1; i >= 0; i--) if (messages[i].role === 'me') { pregunta = i; break }
    if (restaurado) {
      manualRef.current = false
      setPendiente(null)
      if (pregunta >= 0) llevarArriba(pregunta)
    } else if (ultimo.role === 'me') {
      manualRef.current = false
      setPendiente(null)
      llevarArriba(n - 1)
    } else if (manualRef.current) {
      setPendiente(n - 1)
    } else if (pregunta >= 0) {
      llevarArriba(pregunta)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages])

  const onScroll = () => {
    if (programadoRef.current) return
    manualRef.current = true
    if (pendiente !== null) {
      const box = scrollRef.current
      const el = box?.querySelector<HTMLElement>(`[data-msg="${pendiente}"]`)
      if (box && el && el.offsetTop < box.scrollTop + box.clientHeight - 40) setPendiente(null)
    }
  }

  const isBusy =
    sseState.status === 'classifying' ||
    sseState.status === 'searching' ||
    sseState.status === 'thinking' ||
    sseState.status === 'middleware_block'

  const isCancelled = sseState.status === 'cancelled'

  // la respuesta a un botón de acción sin producto nuevo ("No hay una promoción…"): el producto de la consulta
  const productoDeVuelta = (() => {
    const n = messages.length
    if (n < 2) return null
    const ultimo = messages[n - 1], antes = messages[n - 2]
    if (ultimo.role !== 'staff' || ultimo.card || ultimo.triaje || (ultimo.derivacion?.length ?? 0) > 0) return null
    if (antes.role !== 'me' || !ACCIONES.test(antes.text ?? '')) return null
    for (let i = n - 1; i >= 0; i--) {
      const c = messages[i].card
      if (messages[i].role === 'staff' && c?.sku_id) return { idx: i, card: c }
    }
    return null
  })()

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="chat-scroll overflow-y-auto px-4 py-3 grid gap-3 content-start"
      style={{ background: 'var(--color-bg-chat)' }}
    >
      {messages.map((m, idx) => (
        <div key={`w-${idx}-${m.ts}`} data-msg={idx} className="grid">{(() => {
        const key = `${idx}-${m.ts}`
        if (m.role === 'me' && m.text) {
          return (
            <Bubble key={key} variant="me" time={timeOf(m.ts)}>
              {m.text}
            </Bubble>
          )
        }
        if (m.role === 'system' && m.text) {
          return (
            <Bubble key={key} variant="system">
              {m.text}
            </Bubble>
          )
        }
        if (m.role === 'staff' && m.card) {
          const card = m.card
          // 2026-05-12 — fluidez conversacional: chips post-card.
          // 1) suggestions del agente (cuando vienen) — clarificadores
          //    clínicos del caso ("¿hay fiebre?", "¿con sangre?").
          // 2) chip determinístico SIEMPRE post-card resuelto:
          //    "Otra consulta para este cliente" → click foca el input.
          //    Se omite si red flag / derivación (flow_used="failed") porque
          //    en esos casos no aplica seguir conversación comercial.
          const isResolved = card.flow_used !== 'failed' && card.sku_id !== 0
          const agentSuggestions =
            isResolved && m.suggestions && m.suggestions.length > 0
              ? m.suggestions.filter((x) => !/algo m[áa]s|otra consulta|necesita algo|m[áa]s para/i.test(x)
                  // 22-oct: el complemento ya está en la tarjeta con su botón "Sumar"
                  && !((card.complementarios ?? []).length > 0 && /^sumar /i.test(x)))
              : []
          // 2026-10-01 — el cierre ("Otra consulta para este cliente" / "Nuevo cliente") va
          // una sola vez al pie de la última respuesta (CierreRespuesta); acá solo los chips
          // del asistente, sin los de "algo más" que ahora son la puerta de abajo.
          const closingChip: string | null = null
          return (
            <div key={key} className="grid gap-2">
              {m.derivacion && m.derivacion.length > 0 && (
                <DerivacionBanner derivacion={m.derivacion} />
              )}
              {/* 21-oct ("la decisión primero", lienzo aprobado por Elvio): primero el producto, la dosis y
                  el precio, con las acciones dentro de la tarjeta; después lo que hay que decirle al cliente */}
              {fichaEnPanel ? (
                // mismo producto que la respuesta anterior: no se repite el resumen (la ficha sigue a la vista)
                mismoQueAntes(messages, idx) ? null : (
                  <FichaResumen card={card} activa={fichaActiva === idx} onClick={() => onVerFicha?.(idx)} />
                )
              ) : (
              <ProductCard
                card={card}
                trace_id={m.trace_id ?? null}
                consulta_id={m.consulta_id ?? null}
                device_id={device_id}
                acciones={idx === messages.length - 1 && !isBusy && onAccion
                  ? <AccionesProducto card={card} perfil={perfil ?? null} ocupado={isBusy} onAccion={onAccion} />
                  : undefined}
                onSumar={idx === messages.length - 1 && !isBusy ? onPickAlternative : undefined}
              />
              )}
              {m.text && (
                <Bubble variant="staff" time={timeOf(m.ts)}>
                  {m.text}
                </Bubble>
              )}
              {m.guia && <GuiaCliente guia={m.guia} />}
              {!fichaEnPanel && (
                <OpcionesProducto card={card} ocupado={isBusy} onPick={(n) => !isBusy && onPickAlternative(n)} />
              )}
              {(agentSuggestions.length > 0 || closingChip) && (
                <div className="flex flex-wrap gap-2 px-2 pt-1">
                  {agentSuggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      disabled={isBusy}
                      aria-disabled={isBusy}
                      className="text-[13px] rounded-full transition"
                      style={{
                        padding: '6px 12px',
                        background: 'var(--color-brand-soft)',
                        border: '1px solid var(--color-brand)',
                        color: 'var(--color-brand-ink)',
                        cursor: isBusy ? 'not-allowed' : 'pointer',
                        opacity: isBusy ? 0.45 : 1,
                        pointerEvents: isBusy ? 'none' : 'auto',
                      }}
                      onClick={() => {
                        if (isBusy) return
                        onPickAlternative(s)
                      }}
                    >
                      {s}
                    </button>
                  ))}
                  {closingChip && (
                    <button
                      type="button"
                      disabled={isBusy}
                      aria-disabled={isBusy}
                      className="text-[13px] rounded-full transition"
                      style={{
                        padding: '6px 12px',
                        background: 'transparent',
                        border: '1px dashed var(--color-border-strong)',
                        color: 'var(--color-text-muted)',
                        cursor: isBusy ? 'not-allowed' : 'pointer',
                        opacity: isBusy ? 0.45 : 1,
                        pointerEvents: isBusy ? 'none' : 'auto',
                      }}
                      onClick={() => {
                        if (isBusy) return
                        onContinue?.()
                      }}
                      title={
                        isBusy
                          ? 'Esperá la respuesta'
                          : 'Foca el input para otra consulta del mismo cliente'
                      }
                    >
                      {closingChip}
                    </button>
                  )}
                </div>
              )}
              {m.consulta_id && device_id && (
                <div className="px-2">
                  <FeedbackWidget trace_id={m.trace_id ?? null} consulta_id={m.consulta_id} device_id={device_id} venta />
                </div>
              )}
            </div>
          )
        }
        if (m.role === 'staff' && m.text) {
          // Urgencia sin producto: el banner ya dice el mensaje, no lo repetimos.
          const soloBanner = m.derivacion?.[0]?.mensaje === m.text
          return (
            <div key={key} className="grid gap-2">
              {m.derivacion && m.derivacion.length > 0 && (
                <DerivacionBanner derivacion={m.derivacion} />
              )}
              {!soloBanner && (
                <Bubble variant="staff" time={timeOf(m.ts)}>
                  {m.text}
                </Bubble>
              )}
              {m.suggestions && m.suggestions.length > 0 && (
                <div className="flex flex-wrap gap-2 px-2">
                  {m.suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      disabled={isBusy}
                      aria-disabled={isBusy}
                      className="rounded-full border border-brand/30 bg-brand-soft/40 px-3 py-1 text-sm text-brand hover:bg-brand-soft transition"
                      style={{
                        cursor: isBusy ? 'not-allowed' : 'pointer',
                        opacity: isBusy ? 0.45 : 1,
                        pointerEvents: isBusy ? 'none' : 'auto',
                      }}
                      onClick={() => {
                        if (isBusy) return
                        onPickAlternative(s)
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {m.consulta_id && device_id && !m.triaje && (
                <div className="px-2">
                  <FeedbackWidget trace_id={m.trace_id ?? null} consulta_id={m.consulta_id} device_id={device_id}
                    venta={!!m.card?.sku_id} />
                </div>
              )}
            </div>
          )
        }
        return null
      })()}</div>
      ))}

      {!isBusy && productoDeVuelta && onAccion && (
        // Hipermaxi 21-oct: "No hay una promoción…" dejaba la consulta sin producto ni botones; vuelve el
        // producto en pantalla con sus acciones
        <div className="grid gap-2" aria-label="Producto de la consulta">
          <FichaResumen card={productoDeVuelta.card} activa={fichaActiva === productoDeVuelta.idx}
            onClick={() => onVerFicha?.(productoDeVuelta.idx)} />
          <div className="product-card">
            <AccionesProducto card={productoDeVuelta.card} perfil={perfil ?? null} ocupado={isBusy} onAccion={onAccion} />
          </div>
        </div>
      )}

      {!isBusy && messages.length > 0 && messages[messages.length - 1].role === 'staff' && !messages[messages.length - 1].triaje && onAccion && onNuevoCliente && (
        <CierreRespuesta
          card={messages[messages.length - 1].card}
          // las acciones ya están dentro de la tarjeta (en el chat o en la ficha de la derecha)
          conAcciones={!productoDeVuelta && (fichaEnPanel ? fichaActiva !== messages.length - 1 : !messages[messages.length - 1].card)}
          perfil={perfil}
          ocupado={isBusy}
          onAccion={onAccion}
          onPreguntar={onPickAlternative}
          onOtraConsulta={() => onContinue?.()}
          onNuevoCliente={onNuevoCliente}
        />
      )}

      {isBusy && (
        <div className="flex items-center gap-2 flex-wrap">
          <LoadingStageMessage />
          <CancelButton />
        </div>
      )}

      {sseState.status === 'error' && (
        <ErrorInlineMessage message={sseState.message} onRetry={onRetry} />
      )}

      {isCancelled && <Bubble variant="system">Consulta cancelada</Bubble>}

      <div className="chat-fin" aria-hidden="true" />
      {pendiente !== null && (
        <button type="button" className="nueva-respuesta"
          onClick={() => { llevarArriba(pendiente); setPendiente(null); manualRef.current = false }}>
          Nueva respuesta ↓
        </button>
      )}
    </div>
  )
}


/** Resumen del producto en la conversación (pantalla ancha): nombre, SKU, stock y dosis.
 *  Tocarlo trae su ficha completa al panel derecho. */
function FichaResumen({ card, activa, onClick }: { card: ProductCardSchema; activa: boolean; onClick: () => void }) {
  const st = card.stock_sucursal
  const ml = card.dosis_por_peso?.ml
  // "c/0h" no es una dosis (consulta real 8-oct, suero oral): si la fuente no fija intervalo, va el modo
  const freq = card.dosis_por_peso?.frecuencia_h || card.dosis?.frequency_h
  const modo = card.dosis_modo?.texto
  const dosis = modo
    ? (modo.length > 44 ? `${modo.slice(0, 42).trimEnd()}…` : modo)
    : ml
      ? `${ml[0] === ml[1] ? ml[0] : `${ml[0]}–${ml[1]}`} ml${freq ? ` c/${freq}h` : ''}`
      : card.dosis && card.dosis.dose_value
        ? `${card.dosis.dose_value} ${card.dosis.unit}${freq ? ` c/${freq}h` : ''}`
        : null
  return (
    <button type="button" className={`ficha-resumen${activa ? ' activa' : ''}`} onClick={onClick}
      aria-pressed={activa} aria-label={`Ver ficha de ${card.nombre_comercial}`}>
      <span className="ficha-resumen-nombre">{card.nombre_comercial}</span>
      <span className="ficha-resumen-meta">
        <span>SKU {card.sku_id}</span>
        {dosis && <span>{dosis}</span>}
        {st !== undefined && st !== null && (
          <span className={st > 0 ? 'stock-ok' : 'stock-no'}>{st > 0 ? `${st} u. acá` : 'sin stock acá'}</span>
        )}
      </span>
      <span className="ficha-resumen-ver">{activa ? 'en la ficha' : 'ver ficha'}</span>
    </button>
  )
}


function mismoQueAntes(messages: Message[], idx: number): boolean {
  const sku = messages[idx]?.card?.sku_id
  for (let i = idx - 1; i >= 0; i--) {
    const c = messages[i].card
    if (messages[i].role === 'staff' && c) return c.sku_id === sku
  }
  return false
}
